// MapLibre GL JS の薄いラッパー
// 国土地理院タイルレイヤーの初期化、現在地マーカー、軌跡描画の共通実装

import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { getTile, putTile } from './cache.js';

// タイルレイヤー定義(国土地理院)
// 利用条件: 個人・非商用なら無料・許諾不要(2026 時点)
// 参照: https://maps.gsi.go.jp/development/ichiran.html
export const TILE_LAYERS = {
  std: {
    name: '標準',
    type: 'raster',
    url: 'https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png',
    minZoom: 2,
    maxZoom: 18,
    extension: 'png',
  },
  pale: {
    name: '淡色',
    type: 'raster',
    url: 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png',
    minZoom: 5,
    maxZoom: 18,
    extension: 'png',
  },
  relief: {
    name: '色別標高図',
    type: 'raster',
    url: 'https://cyberjapandata.gsi.go.jp/xyz/relief/{z}/{x}/{y}.png',
    minZoom: 5,
    maxZoom: 15,
    extension: 'png',
  },
  photo: {
    name: '航空写真',
    type: 'raster',
    url: 'https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg',
    minZoom: 2,
    maxZoom: 18,
    extension: 'jpg',
  },
  vector: {
    name: 'ベクター(高画質)',
    type: 'vector',
    // 国土地理院公式の MapLibre/Mapbox GL JS 用スタイル(github Pages、CORS 許可)
    styleUrl: 'https://gsi-cyberjapan.github.io/gsivectortile-mapbox-gl-js/std.json',
    minZoom: 4,
    maxZoom: 17,
  },
};

const GSI_ATTRIBUTION =
  '<a href="https://www.gsi.go.jp/" target="_blank" rel="noopener">国土地理院</a>';

// MapLibre 地図インスタンス生成
//   container: HTMLElement
//   options:
//     center: [lng, lat]
//     zoom: number
//     layer: 'std' | 'pale' | 'relief' | 'photo'
//     interactive: boolean
export function createMap(container, options = {}) {
  const layerKey = options.layer || 'std';
  const layer = TILE_LAYERS[layerKey];
  const center = options.center || [138.7274, 35.3606]; // 富士山
  const zoom = options.zoom ?? 12;

  const style = layer.type === 'vector' ? layer.styleUrl : buildStyle(layerKey);

  const map = new maplibregl.Map({
    container,
    style,
    center,
    zoom,
    minZoom: layer.minZoom,
    maxZoom: layer.maxZoom,
    attributionControl: false,
    interactive: options.interactive !== false,
    transformRequest: (url) => ({ url }),
  });

  map.addControl(
    new maplibregl.AttributionControl({
      compact: true,
      customAttribution: GSI_ATTRIBUTION,
    })
  );

  return map;
}

// レイヤー切替用 style 生成
function buildStyle(layerKey) {
  const layer = TILE_LAYERS[layerKey];
  return {
    version: 8,
    sources: {
      [`gsi-${layerKey}`]: {
        type: 'raster',
        tiles: [layer.url],
        tileSize: 256,
        minzoom: layer.minZoom,
        maxzoom: layer.maxZoom,
        attribution: GSI_ATTRIBUTION,
      },
    },
    layers: [
      {
        id: `gsi-${layerKey}-layer`,
        source: `gsi-${layerKey}`,
        type: 'raster',
      },
    ],
  };
}

// レイヤー切替
export function switchLayer(map, layerKey) {
  const layer = TILE_LAYERS[layerKey];
  if (!layer) return;
  if (layer.type === 'vector') {
    map.setStyle(layer.styleUrl);
  } else {
    map.setStyle(buildStyle(layerKey));
  }
}

// ============ 現在地マーカー ============
// MapLibre の Marker を使う

export function createPositionMarker(map, lng, lat, opts = {}) {
  const el = document.createElement('div');
  el.className = 'gps-marker';
  const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
    .setLngLat([lng, lat])
    .addTo(map);
  return marker;
}

// 現在地の精度円(GeoJSON ソース)
// 半径(m) を視覚化、地図の見た目に合わせて自動スケール
export function setupAccuracyCircle(map) {
  // 初期化(空のソース・レイヤー)
  if (!map.getSource('accuracy-circle')) {
    map.addSource('accuracy-circle', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: 'accuracy-circle-fill',
      source: 'accuracy-circle',
      type: 'fill',
      paint: {
        'fill-color': '#0a0a0a',
        'fill-opacity': 0.08,
      },
    });
    map.addLayer({
      id: 'accuracy-circle-line',
      source: 'accuracy-circle',
      type: 'line',
      paint: {
        'line-color': '#0a0a0a',
        'line-opacity': 0.2,
        'line-width': 1,
      },
    });
  }
}

export function updateAccuracyCircle(map, lng, lat, accuracyMeters) {
  if (!map.getSource('accuracy-circle')) return;
  const feature = circleFeature(lng, lat, accuracyMeters);
  map.getSource('accuracy-circle').setData({
    type: 'FeatureCollection',
    features: [feature],
  });
}

// 円ポリゴン生成(64角形で近似)
function circleFeature(lng, lat, radiusMeters) {
  const points = 64;
  const coords = [];
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const [dx, dy] = offsetMetersToDegrees(lat, radiusMeters * Math.cos(angle), radiusMeters * Math.sin(angle));
    coords.push([lng + dx, lat + dy]);
  }
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [coords] },
    properties: {},
  };
}

// メートル → 経緯度差(球面近似)
function offsetMetersToDegrees(lat, dxMeters, dyMeters) {
  const EARTH = 6378137;
  const dLat = (dyMeters / EARTH) * (180 / Math.PI);
  const dLng = (dxMeters / (EARTH * Math.cos((lat * Math.PI) / 180))) * (180 / Math.PI);
  return [dLng, dLat];
}

// ============ 軌跡描画 ============

export function setupTrackLayer(map, layerId = 'track', color = '#0a0a0a', width = 4) {
  if (!map.getSource(layerId)) {
    map.addSource(layerId, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: layerId,
      source: layerId,
      type: 'line',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': color, 'line-width': width },
    });
  }
}

export function updateTrack(map, layerId, points) {
  if (!map.getSource(layerId)) return;
  const coords = points.map((p) => [p.lng, p.lat]);
  map.getSource(layerId).setData({
    type: 'FeatureCollection',
    features: coords.length >= 2 ? [
      {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {},
      },
    ] : [],
  });
}

// ============ OSM 登山道オーバーレイ ============
// OpenStreetMap の highway=path/footway/track + sac_scale を赤い破線で表示
//
// データ取得は osm-hiking.js の fetchHikingPaths() で行う。
// map に対しては source/layer の初期化と setData だけを担当。

export function setupOsmHikingLayer(map) {
  if (map.getSource('osm-hiking')) return;
  map.addSource('osm-hiking', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  // 影(下層: 太め薄め)で視認性向上
  map.addLayer({
    id: 'osm-hiking-shadow',
    source: 'osm-hiking',
    type: 'line',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#ffffff',
      'line-width': 5,
      'line-opacity': 0.6,
    },
  });
  // 本線(赤の破線)
  map.addLayer({
    id: 'osm-hiking-layer',
    source: 'osm-hiking',
    type: 'line',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#dc2626',
      'line-width': 2.5,
      'line-opacity': 0.85,
      'line-dasharray': [2, 1.5],
    },
  });
}

export function updateOsmHiking(map, geojson) {
  if (!map.getSource('osm-hiking')) return;
  map.getSource('osm-hiking').setData(geojson || { type: 'FeatureCollection', features: [] });
}

// ============ 過去軌跡オーバーレイ(足跡レイヤー) ============
// 自分が過去に歩いた道を半透明の細線で重ね表示する。
// 「他者の足跡」ではなく「自分の歩いた道」=独自登山道ヒートマップ。

export function setupFootprintsLayer(map) {
  if (map.getSource('footprints')) return;
  map.addSource('footprints', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: 'footprints-layer',
    source: 'footprints',
    type: 'line',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#1d4ed8',
      'line-width': 3,
      'line-opacity': 0.35,
    },
  });
}

export function updateFootprints(map, tracks) {
  if (!map.getSource('footprints')) return;
  const features = (tracks || [])
    .map((t) => {
      const coords = (t.points || [])
        .filter((p) => typeof p.lat === 'number' && typeof p.lng === 'number')
        .map((p) => [p.lng, p.lat]);
      if (coords.length < 2) return null;
      return {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { id: t.id, name: t.name || '' },
      };
    })
    .filter(Boolean);
  map.getSource('footprints').setData({ type: 'FeatureCollection', features });
}

// localStorage キーで一括管理
export function isFootprintsEnabled() {
  return localStorage.getItem('sentier.showFootprints') === '1';
}

export function setFootprintsEnabled(on) {
  if (on) localStorage.setItem('sentier.showFootprints', '1');
  else localStorage.removeItem('sentier.showFootprints');
}

// ============ MapLibre 再エクスポート ============
export { maplibregl };
